import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';


const SystemActivationStep = ({ data, allSetupData, onComplete, onBack, onCancel, loading }) => {
  const [validationStatus, setValidationStatus] = useState({
    organizationProfile: 'pending',
    bankIntegration: 'pending',
    payrollRules: 'pending',
    testPayroll: 'pending'
  });
  const [validationStarted, setValidationStarted] = useState(false);
  const [allValidated, setAllValidated] = useState(false);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    if (validationStarted) {
      runValidation();
    }
  }, [validationStarted]);

  const runValidation = async () => {
    // Simulate validation process
    const validationSteps = [
      { key: 'organizationProfile', delay: 1000 },
      { key: 'bankIntegration', delay: 1500 },
      { key: 'payrollRules', delay: 1200 },
      { key: 'testPayroll', delay: 2000 }
    ];

    for (const step of validationSteps) {
      await new Promise(resolve => setTimeout(resolve, step.delay));
      setValidationStatus(prev => ({
        ...prev,
        [step?.key]: 'completed'
      }));
    }

    setAllValidated(true);
  };

  const handleStartValidation = () => {
    setValidationStarted(true);
  };

  const handleActivate = async () => {
    try {
      setActivating(true);
      
      // Create team access records (simplified - just mark as configured)
      // In a real implementation, this would create user_role_assignments
      
      onComplete?.({
        validationCompleted: true,
        activatedAt: new Date()?.toISOString(),
        teamAccessConfigured: true // Mark team access as configured
      });
    } catch (err) {
      console.error('Error activating system:', err);
    } finally {
      setActivating(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <Icon name="CheckCircle" size={20} className="text-emerald-600" />;
      case 'pending':
        return validationStarted ? (
          <div className="animate-spin">
            <Icon name="Loader" size={20} className="text-amber-600" />
          </div>
        ) : (
          <Icon name="Circle" size={20} className="text-gray-400" />
        );
      default:
        return <Icon name="Circle" size={20} className="text-gray-400" />;
    }
  };

  const validationChecks = [
    {
      key: 'organizationProfile',
      title: 'Organization Profile Validation',
      description: 'Verifying company details and ABN/TIN numbers',
      details: [
        `Company: ${allSetupData?.organizationProfile?.companyName || 'N/A'}`,
        `Industry: ${allSetupData?.organizationProfile?.industryType || 'N/A'}`,
        `Employees: ${allSetupData?.organizationProfile?.employeeCount || 'N/A'}`
      ]
    },
    {
      key: 'bankIntegration',
      title: 'Bank Integration Verification',
      description: 'Testing connections to PNG financial institutions',
      details: [
        `Banks: ${allSetupData?.bankIntegration?.selectedBanks?.length || 0} configured`,
        `NASFUND: ${allSetupData?.bankIntegration?.nasfundIntegration ? 'Enabled' : 'Disabled'}`,
        `Routing: ${allSetupData?.bankIntegration?.payrollRouting || 'N/A'}`
      ]
    },
    {
      key: 'payrollRules',
      title: 'Payroll Rules Compliance Check',
      description: 'Validating against PNG Employment Act requirements',
      details: [
        `Pay Period: ${allSetupData?.payrollRules?.payPeriod || 'N/A'}`,
        `Overtime Rate: ${allSetupData?.payrollRules?.overtimeRate || 'N/A'}x`,
        `NASFUND Rates: ${allSetupData?.payrollRules?.nasfundEmployeeRate || 'N/A'}% / ${allSetupData?.payrollRules?.nasfundEmployerRate || 'N/A'}%`
      ]
    },
    {
      key: 'testPayroll',
      title: 'Test Payroll Generation',
      description: 'Running sample payroll calculation',
      details: [
        'Sample employee payroll calculated',
        'Tax calculations verified',
        'Bank file format validated'
      ]
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <Icon name="CheckCircle" size={24} className="text-emerald-600 mr-2" />
          <h2 className="text-2xl font-bold text-gray-900">System Activation</h2>
        </div>
        <p className="text-gray-600">
          Final validation and system activation. We'll verify all configurations and generate a test payroll.
        </p>
      </div>
      {/* Validation Checks */}
      <div className="space-y-4 mb-6">
        {validationChecks?.map(check => (
          <div
            key={check?.key}
            className={`border rounded-lg p-4 transition-all ${
              validationStatus?.[check?.key] === 'completed'
                ? 'border-emerald-300 bg-emerald-50' :'border-gray-200'
            }`}
          >
            <div className="flex items-start">
              <div className="mr-3 mt-1">
                {getStatusIcon(validationStatus?.[check?.key])}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{check?.title}</h4>
                <p className="text-sm text-gray-600 mt-1">{check?.description}</p>
                {validationStatus?.[check?.key] === 'completed' && (
                  <div className="mt-2 space-y-1">
                    {check?.details?.map((detail, idx) => (
                      <p key={idx} className="text-xs text-emerald-700 flex items-center">
                        <Icon name="Check" size={14} className="mr-1" />
                        {detail}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Validation Button */}
      {!validationStarted && (
        <div className="mb-6">
          <Button
            type="button"
            onClick={handleStartValidation}
            iconName="Play"
            size="lg"
            className="w-full"
          >
            Start Validation
          </Button>
        </div>
      )}
      {/* Success Message */}
      {allValidated && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-6">
          <div className="flex items-start">
            <Icon name="CheckCircle" size={24} className="text-emerald-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-emerald-900 mb-2">
                ✓ All Validations Passed!
              </h3>
              <p className="text-sm text-emerald-700 mb-4">
                Your system is ready to activate. Click the button below to complete setup and access your dashboard.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-white rounded-lg p-3 border border-emerald-200">
                  <p className="font-semibold text-emerald-700 text-sm">✓ Organization Configured</p>
                  <p className="text-xs text-gray-600 mt-1">Ready for employee onboarding</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-emerald-200">
                  <p className="font-semibold text-emerald-700 text-sm">✓ Banks Connected</p>
                  <p className="text-xs text-gray-600 mt-1">Automated payroll transfers enabled</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-emerald-200">
                  <p className="font-semibold text-emerald-700 text-sm">✓ Compliance Active</p>
                  <p className="text-xs text-gray-600 mt-1">PNG Employment Act rules applied</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Welcome Dashboard Info */}
      {allValidated && (
        <div className="bg-gradient-to-r from-amber-50 to-emerald-50 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Icon name="Sparkles" size={20} className="text-amber-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-gray-900">Welcome Dashboard Ready</p>
              <p className="text-sm text-gray-700 mt-1">
                Your personalized dashboard is configured with quick-start guides, sample data, and recommended next steps.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            iconName="ArrowLeft"
            disabled={validationStarted || activating}
          >
            Back
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            iconName="X"
            disabled={validationStarted || activating}
          >
            Cancel
          </Button>
        </div>
        <Button
          type="button"
          onClick={handleActivate}
          iconName="Rocket"
          iconPosition="right"
          disabled={!allValidated}
          loading={loading || activating}
          size="lg"
        >
          Activate System & Launch Dashboard
        </Button>
      </div>
    </div>
  );
};

export default SystemActivationStep;